/**
 * PRV-008: Actions para Solicitud de Edición de Factibilidad Corporativa
 *
 * Hooks de React Query para manejar las operaciones CRUD de solicitudes de edición.
 */

import { erpApi } from "@/shared/axios";
import { ROUTER_PATHS, urlBackend } from "@/shared/constants";
import { AlertSeverityEnum, UseMutationsParams } from "@/shared/interfaces";
import {
  ISolicitudEdicionFactibilidad,
  ISolicitudEdicionFactibilidadParams,
  ISolicitudEdicionFactibilidadResp
} from "@/shared/interfaces/erp/factibilidad_corporativo";
import { getUrlParamsFetch, handleAxiosError } from "@/shared/utils";
import { useUiStore } from "@/store/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const { get, post, put } = erpApi();

// Query key para invalidar cache
const QUERY_KEY = 'solicitudedicionfactibilidad';

/**
 * Hook para obtener listado de solicitudes de edición con filtros y paginación
 */
export const useFetchGetSolicitudesEdicion = (
  params: ISolicitudEdicionFactibilidadParams,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: [QUERY_KEY, ...Object.values(params || {})],
    queryFn: async () => {
      return await getSolicitudesEdicion(params ?? {});
    },
    refetchOnWindowFocus: false,
    enabled
  });
};

export const getSolicitudesEdicion = async (data: ISolicitudEdicionFactibilidadParams) => {
  const queryParams = getUrlParamsFetch(data || {});
  return await get<ISolicitudEdicionFactibilidadResp>(
    `${urlBackend}api/v1/solicitudedicionfactibilidad/?${queryParams}`,
    true
  );
};

/**
 * Hook para obtener una solicitud de edición por ID
 */
export const useFetchGetByIdSolicitudEdicion = (id: string) => {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: async () => {
      return await getByIdSolicitudEdicion(id);
    },
    refetchOnWindowFocus: false,
    gcTime: 0,
  });
};

export const getByIdSolicitudEdicion = async (id: string) => {
  return await get<ISolicitudEdicionFactibilidad>(
    `${urlBackend}api/v1/solicitudedicionfactibilidad/${id}/`,
    true
  );
};

/**
 * Hook para crear una nueva solicitud de edición
 * Usado por el asesor desde Seguimiento y Coste
 */
export const useFetchPostSolicitudEdicion = ({
  enableSuccessAlert = true,
  navigate,
  enableRedirect = false,
  returnUrl
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore(s => s.setAlert);

  return useMutation({
    mutationFn: postSolicitudEdicion,
    onSuccess: (data) => {
      // Invalidar cache de solicitudes y factibilidades
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['factibilidadcoorporativa'] });

      enableSuccessAlert &&
        setAlert(true, data.message || 'Solicitud de edición creada exitosamente', AlertSeverityEnum.success);

      navigate && enableRedirect &&
        navigate(returnUrl ?? ROUTER_PATHS.ventasCorporativas.seguimientoCosteNav);
    },
    onError: (error) => handleAxiosError(error)
  });
};

export const postSolicitudEdicion = async (data: ISolicitudEdicionFactibilidadParams) => {
  return await post<ISolicitudEdicionFactibilidad>(
    `${urlBackend}api/v1/solicitudedicionfactibilidad/`,
    data,
    true
  );
};

/**
 * Hook para actualizar una solicitud de edición (aprobar/rechazar/cancelar)
 * Usado por el supervisor desde Solicitudes Pendientes
 */
export const useFetchPutSolicitudEdicion = ({
  enableSuccessAlert = true,
  navigate,
  enableRedirect = false,
  returnUrl,
  inSiteRefresh = true
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setAlert = useUiStore(s => s.setAlert);

  return useMutation({
    mutationFn: putSolicitudEdicion,
    onSuccess: (data) => {
      if (inSiteRefresh) {
        // Invalidar cache de solicitudes y factibilidades
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: ['factibilidadcoorporativa'] });
      }

      enableSuccessAlert &&
        setAlert(true, data.message || 'Solicitud actualizada exitosamente', AlertSeverityEnum.success);

      navigate && enableRedirect &&
        navigate(returnUrl ?? ROUTER_PATHS.ventasCorporativas.solicitudesPendientesCorporativoNav);
    },
    onError: (error) => handleAxiosError(error)
  });
};

export const putSolicitudEdicion = async ({
  id,
  data
}: {
  id: string;
  data: ISolicitudEdicionFactibilidadParams;
}) => {
  return await put<ISolicitudEdicionFactibilidad>(
    `${urlBackend}api/v1/solicitudedicionfactibilidad/${id}/`,
    data,
    true
  );
};
